import { defineComponent as _defineComponent } from 'vue'
import { store } from '@/store';
import { useCheckoutPOS } from '@/store/checkout.pos.composable';
import { StorageService } from '@/core';
import { SiteMode } from './site-mode';
import PageService from '@/core/page.service';
import CheckoutService from '@/services/checkout.service';
import CheckoutPageViewModel = Vertica.LouisPoulsen.Application.Content.Pages.Checkout.CheckoutPageViewModel;
import SaveCheckoutWebRequest = Vertica.LouisPoulsen.Application.Commerce.Requests.PointOfSale.SaveCheckoutWebRequest;
import CheckoutSummaryPOS from '@/ui/views/checkout/CheckoutSummaryPOS.vue';
import TrackingService from '@/core/tracking.service';
import CustomerDetailsPOS from './CustomerDetailsPOS.vue';
import CustomerDetailsPOSJapan from './CustomerDetailsPOSJapan.vue';
import DeliveryMethodsPOS from './DeliveryMethodsPOS.vue';
import { useAddressAutoComplete } from '@/services/address-auto-complete.service';
import { computed, nextTick, onMounted, ref } from 'vue';
import { PaymentProvider } from '@/enums';
import { useValidator } from '@/core/app.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'CheckoutPOS',
  props: {
    model: null
  },
  setup(__props: any) {

const props = __props as { model: CheckoutPageViewModel }

const siteMode = SiteMode;


const basket = computed<BasketViewModel>(() => store.getters['basket/basket']);
const isBasketLocked = computed<boolean>(() => store.getters['basket/isBasketLocked']);

const termsUrl = computed<string>(() => store.getters['app/termsUrl']);

const { getCountryTaxTotal, submitOrder } = useCheckoutPOS();

const customer = ref<InvoiceAddressPOS>({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    company: '',
    address: '',
    postalCode: '',
    city: '',
    countryCode: '',
    extendedFields: {
        stateCode: '',
        address2: ''
    }
});

// Edited through DeliveryMethodsPOS
const deliveryInfo = ref<ShippingAddressPOS>({
    phone: '',
    firstName: '',
    lastName: '',
    address: '',
    postalCode: '',
    city: '',
    countryCode: '',
    extendedFields: {
        stateCode: '',
        address2: ''
    },
    company: ''
});

const selectedDeliveryMethod = ref<string>('');
const selectedPickupShopIdValue = ref<string>('');
const selectedPickupShopValue = ref<any>({});

const alternativeDelivery = ref<boolean>(false);

const comment = ref<string>('');
const hasAcceptedTerm = ref<boolean>(false);
const selectedCustomerCountry = ref<InvoiceAddressCountryViewObject>({} as InvoiceAddressCountryViewObject);
const selectedShippingCountry = ref<AddressCountryViewObject>({} as AddressCountryViewObject);

const selectedPaymentProviderValue = ref<string>(PaymentProvider.Dibs.toString());
const taxCalculationErrorMessage = ref<string>(null);
const countryTaxTotal = ref<string>(null);

const orderRequest = computed<SaveCheckoutWebRequest>(() => {
    return {
        customer: customer.value,
        alternativeDelivery: deliveryInfo.value,
        deliveryMethod: selectedDeliveryMethod.value,
        pickupShop: selectedPickupShopIdValue.value,
        useAlternativeDeliveryAddress: alternativeDelivery.value,
        comment: comment.value,
        paymentProvider: selectedPaymentProvider.value
    };
});

const selectedPaymentProvider = computed<number>(() => {
    return parseInt(selectedPaymentProviderValue.value, 10);
});

const { script, yubinBangoLoaded, setUpAutocompleteAddressForJapanMarket } = useAddressAutoComplete();

onMounted(() => {
    fetchFormData();
    comment.value = props.model.comment;

    if (props.model.paymentProvider) {
        selectedPaymentProviderValue.value = props.model.paymentProvider.toString();
    }

    if (props.model.isJapanMarket) {
        setUpAutocompleteAddressForJapanMarket();
    }
    nextTick(() => {
        updateTaxTotalForCountry();
    });

    if (!props.model.hasSubmittedMarketingEmailPermission) {
        TrackingService.event('Newsletter in checkout', 'page2', 'CheckoutNudge');
    }
});

async function updateShippingCountry(country: AddressCountryViewObject) {
    selectedShippingCountry.value = country;
    await updateTaxTotalForCountry();
}

function setAlternativeDelivery(alternativeDeliveryValue: boolean) {
    alternativeDelivery.value = alternativeDeliveryValue;
}

function setDeliveryMethod(deliveryMethodId: string) {
    selectedDeliveryMethod.value = deliveryMethodId;
}

function setSelectedPickupShop(selectedPickupShop: any, selectedPickupShopId: string) {
    selectedPickupShopValue.value = selectedPickupShop;
    selectedPickupShopIdValue.value = selectedPickupShopId;
}

const mandatoryAlternativeDelivery = computed<boolean>(() => {
    return props.model.shippingAddressCountries.map(country => country.countryCode).indexOf(selectedCustomerCountry.value.id) < 0;
});

function onCustomerCountryChanged(country: InvoiceAddressCountryViewObject) {
    if (country !== undefined) {
        selectedCustomerCountry.value = country;
        customer.value.countryCode = country.id;
    }
    setCustomerInLocalStorage(CheckoutService.customerLocalStorageKey, customer.value);

    updateTaxTotalForCountry();
}

const $validator = useValidator();

function validateAndProcessOrder() {
    $validator
        .validateAll()
        .then(isValid => {
            if (isValid) {
                submitOrder(orderRequest.value).then(response => {
                    console.log(response.redirectUrl);
                    PageService.hardRedirect(response.redirectUrl);
                });
            } else {
                scrollInvalidElementIntoView();
            }
        })
        .catch(x => console.log('catch' + x));
}

function setCustomerInLocalStorage(key, value) {
    StorageService.setItemAs(key, value);
}

function fetchFormData() {
    // Customer form data
    customer.value = getCustomerData();
    // Alternative delivery address form data
    deliveryInfo.value = getDeliveryInfoData();
}

function getCustomerData(): InvoiceAddressPOS {
    // Get from local storage
    const customer: InvoiceAddressPOS = StorageService.getItemAs(CheckoutService.customerLocalStorageKey);

    // If no storage then return server object
    if (!customer) {
        return {
            email: props.model.customer.email || props.model.marketingEmailPermissionEmail,
            phone: props.model.customer.phone,
            firstName: props.model.customer.firstName,
            lastName: props.model.customer.lastName,
            company: props.model.customer.company,
            address: props.model.customer.address,
            postalCode: props.model.customer.postalCode,
            city: props.model.customer.city,
            countryCode: props.model.customer.country.countryCode,
            extendedFields: {
                stateCode: props.model.customer.stateCode,
                address2: props.model.customer.address2
            }
        };
    }

    return {
        email: customer.email || props.model.marketingEmailPermissionEmail,
        phone: customer.phone,
        firstName: customer.firstName,
        lastName: customer.lastName,
        company: customer.company,
        address: customer.address,
        postalCode: customer.postalCode,
        city: customer.city,
        countryCode: customer.countryCode ? customer.countryCode : props.model.customer.country.countryCode,
        extendedFields: {
            stateCode: customer.extendedFields ? customer.extendedFields.stateCode : '',
            address2: customer.extendedFields ? customer.extendedFields.address2 : ''
        }
    };
}

async function updateTaxTotalForCountry() {
    if (props.model.isUsMarket) {
        return;
    }

    let countryCode = null;

    if (!alternativeDelivery.value && customer.value.countryCode) {
        countryCode = customer.value.countryCode;
    }

    if (alternativeDelivery.value && selectedShippingCountry.value.countryCode) {
        countryCode = selectedShippingCountry.value.countryCode;
    }

    if (!countryCode) {
        countryTaxTotal.value = null;
        return;
    }

    countryTaxTotal.value = (await getCountryTaxTotal(countryCode)).taxTotal;
}

function getDeliveryInfoData(): ShippingAddressPOS {
    // Get from local storage
    const deliveryInfo: ShippingAddressPOS = StorageService.getItemAs(CheckoutService.deliveryInfoLocalStorageKey);

    // If no storage then return server object
    if (!deliveryInfo) {
        return {
            phone: props.model.alternativeDelivery.phone,
            firstName: props.model.alternativeDelivery.firstName,
            lastName: props.model.alternativeDelivery.lastName,
            address: props.model.alternativeDelivery.address,
            postalCode: props.model.alternativeDelivery.postalCode,
            city: props.model.alternativeDelivery.city,
            countryCode: props.model.alternativeDelivery.country.countryCode,
            extendedFields: {
                address2: props.model.alternativeDelivery.address2,
                stateCode: props.model.alternativeDelivery.stateCode
            },
            company: props.model.alternativeDelivery.company
        };
    }

    return {
        phone: deliveryInfo.phone,
        firstName: deliveryInfo.firstName,
        lastName: deliveryInfo.lastName,
        address: deliveryInfo.address,
        postalCode: deliveryInfo.postalCode,
        city: deliveryInfo.city,
        countryCode: deliveryInfo.countryCode,
        extendedFields: deliveryInfo.extendedFields,
        company: deliveryInfo.company
    };
}

function scrollInvalidElementIntoView() {
    const firstInvalidElement = document.querySelector('.is-danger');
    if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({ behavior: 'smooth' });
    }
}


return { __sfc: true,siteMode, props, basket, isBasketLocked, termsUrl, getCountryTaxTotal, submitOrder, customer, deliveryInfo, selectedDeliveryMethod, selectedPickupShopIdValue, selectedPickupShopValue, alternativeDelivery, comment, hasAcceptedTerm, selectedCustomerCountry, selectedShippingCountry, selectedPaymentProviderValue, taxCalculationErrorMessage, countryTaxTotal, orderRequest, selectedPaymentProvider, script, yubinBangoLoaded, setUpAutocompleteAddressForJapanMarket, updateShippingCountry, setAlternativeDelivery, setDeliveryMethod, setSelectedPickupShop, mandatoryAlternativeDelivery, onCustomerCountryChanged, $validator, validateAndProcessOrder, setCustomerInLocalStorage, fetchFormData, getCustomerData, updateTaxTotalForCountry, getDeliveryInfoData, scrollInvalidElementIntoView, CheckoutSummaryPOS, CustomerDetailsPOS, CustomerDetailsPOSJapan, DeliveryMethodsPOS }
}

})